import { useEffect, useMemo, useState } from "react";
import { api, API, auth } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { CalendarClock, MapPin, User2, Plus, Trash2, Pencil, QrCode, Users, RefreshCw, Search, ExternalLink, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";

const EMPTY_EVENT = {
  title: "",
  description: "",
  event_type: "one-time",
  start_datetime: "",
  end_datetime: "",
  venue: "",
  trainer_name: "",
  hospital_id: "",
  status: "Active",
  recurrence: { pattern: "weekly", interval: 1, end_date: "" },
};

function toInputDt(iso) {
  if (!iso) return "";
  try { return iso.slice(0, 16); } catch { return ""; }
}

export default function Events() {
  const [events, setEvents] = useState(null);
  const [locs, setLocs] = useState([]);
  const [q, setQ] = useState("");
  const [filterHosp, setFilterHosp] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // event being edited
  const [form, setForm] = useState(EMPTY_EVENT);
  const [qrEvent, setQrEvent] = useState(null); // event for QR view
  const [attendeesOf, setAttendeesOf] = useState(null); // event with attendees
  const [confirmDel, setConfirmDel] = useState(null);

  async function load() {
    const params = {};
    if (filterHosp) params.hospital_id = filterHosp;
    const [e, l] = await Promise.all([
      api.get("/events", { params }),
      api.get("/masters/locations"),
    ]);
    setEvents(e.data); setLocs(l.data || []);
  }
  useEffect(() => { load(); }, [filterHosp]);

  const filtered = useMemo(() => {
    if (!events) return [];
    if (!q) return events;
    const s = q.toLowerCase();
    return events.filter(e =>
      (e.title || "").toLowerCase().includes(s) ||
      (e.venue || "").toLowerCase().includes(s) ||
      (e.trainer_name || "").toLowerCase().includes(s)
    );
  }, [events, q]);

  const locById = useMemo(() => Object.fromEntries(locs.map(l => [l.id, l])), [locs]);

  function openNew() {
    setEditing(null);
    setForm(EMPTY_EVENT);
    setDialogOpen(true);
  }
  function openEdit(ev) {
    setEditing(ev);
    setForm({
      ...EMPTY_EVENT,
      ...ev,
      start_datetime: toInputDt(ev.start_datetime),
      end_datetime: toInputDt(ev.end_datetime),
      hospital_id: ev.hospital_id || "",
      recurrence: ev.recurrence || EMPTY_EVENT.recurrence,
    });
    setDialogOpen(true);
  }

  async function save() {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.start_datetime || !form.end_datetime) { toast.error("Start & end date/time required"); return; }
    const body = {
      ...form,
      hospital_id: form.hospital_id || null,
      recurrence: form.event_type === "recurring" ? form.recurrence : null,
    };
    try {
      if (editing) await api.put(`/events/${editing.id}`, body);
      else await api.post("/events", body);
      setDialogOpen(false);
      load();
      toast.success(editing ? "Event updated" : "Event created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  }

  async function del(ev) {
    await api.delete(`/events/${ev.id}`);
    setConfirmDel(null);
    load();
    toast.success("Event deleted");
  }

  async function openAttendees(ev) {
    const r = await api.get(`/events/${ev.id}`);
    setAttendeesOf(r.data);
  }

  async function downloadTemplate() {
    try {
      const resp = await fetch(`${API}/events/import-template`, {
        headers: { Authorization: `Bearer ${auth.token()}` },
      });
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "event-import-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template download failed"); }
  }

  async function onImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const resp = await fetch(`${API}/events/bulk-import`, {
        method: "POST", headers: { Authorization: `Bearer ${auth.token()}` }, body: fd,
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(d?.detail || "Import failed");
      const desc = `${d.created_count} created${d.rejected_count ? ` · ${d.rejected_count} rejected` : ""}`;
      if (d.rejected_count) toast.warning("Import completed with issues", { description: desc });
      else toast.success("Import complete", { description: desc });
      load();
    } catch (err) {
      toast.error(err?.message || "Import failed");
    } finally {
      e.target.value = "";
    }
  }

  return (
    <div className="p-6 lg:p-10 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-600 font-bold">Training Events</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl text-slate-900 mt-1">Event Master</h1>
          <p className="text-slate-500 mt-1">Schedule one-time or recurring trainings, print QR posters for kiosk attendance.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="event-template-btn"><Download className="h-4 w-4 mr-1"/>Template</Button>
          <label className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium cursor-pointer" data-testid="event-import-label">
            <Upload className="h-4 w-4"/>Import
            <input type="file" accept=".xlsx" className="hidden" onChange={onImport} data-testid="event-import-input"/>
          </label>
          <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-1"/>Refresh</Button>
          <Button onClick={openNew} className="bg-sky-500 hover:bg-sky-600" data-testid="event-new-btn"><Plus className="h-4 w-4 mr-1"/>New Event</Button>
        </div>
      </div>

      <Card className="border-slate-200 rounded-2xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px] relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title / venue / trainer…" className="pl-9" data-testid="event-search"/>
            </div>
            <div className="min-w-[220px]">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Hospital / Location</Label>
              <Select value={filterHosp || "__all__"} onValueChange={v => setFilterHosp(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="event-filter-hospital"><SelectValue placeholder="All"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All Locations</SelectItem>
                  {locs.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="events-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="py-3 px-4">Title</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Start</th>
                  <th className="py-3 px-4">Venue</th>
                  <th className="py-3 px-4">Hospital</th>
                  <th className="py-3 px-4 text-center">Attendees</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {events === null && (
                  <tr><td colSpan="8" className="py-10 text-center text-slate-400">Loading…</td></tr>
                )}
                {events !== null && filtered.length === 0 && (
                  <tr><td colSpan="8" className="py-10 text-center text-slate-400">No events. Create your first one above.</td></tr>
                )}
                {filtered.map(ev => (
                  <tr key={ev.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`event-row-${ev.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-slate-900">{ev.title}</div>
                      {ev.trainer_name && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><User2 className="h-3 w-3"/>{ev.trainer_name}</div>}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="secondary" className={ev.event_type === "recurring" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-700"}>
                        {ev.event_type === "recurring" ? `Recurring · ${ev.recurrence?.pattern || "?"}` : "One-time"}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="text-slate-900">{new Date(ev.start_datetime).toLocaleDateString()}</div>
                      <div className="text-xs text-slate-500">{new Date(ev.start_datetime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                    </td>
                    <td className="py-3 px-4">
                      {ev.venue ? <span className="inline-flex items-center gap-1 text-slate-700"><MapPin className="h-3 w-3 text-slate-400"/>{ev.venue}</span> : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td className="py-3 px-4 text-slate-600">{locById[ev.hospital_id]?.name || <span className="text-slate-300 text-xs">—</span>}</td>
                    <td className="py-3 px-4 text-center">
                      <button onClick={() => openAttendees(ev)} className="text-sky-600 hover:underline font-bold" data-testid={`event-attendees-${ev.id}`}>
                        {ev.attendee_count || 0}
                      </button>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="secondary" className={
                        ev.status === "Active" ? "bg-emerald-100 text-emerald-700" :
                        ev.status === "Cancelled" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700"
                      }>{ev.status}</Badge>
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setQrEvent(ev)} data-testid={`event-qr-${ev.id}`}><QrCode className="h-4 w-4"/></Button>
                        <Button size="sm" variant="outline" onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`}><Pencil className="h-4 w-4"/></Button>
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDel(ev)} data-testid={`event-delete-${ev.id}`}><Trash2 className="h-4 w-4 text-red-500"/></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Event create/edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Event" : "New Event"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="event-form-title"/>
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}/>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.event_type} onValueChange={v => setForm({ ...form, event_type: v })}>
                <SelectTrigger data-testid="event-form-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one-time">One-time</SelectItem>
                  <SelectItem value="recurring">Recurring</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Start *</Label>
              <Input type="datetime-local" value={form.start_datetime} onChange={e => setForm({ ...form, start_datetime: e.target.value })} data-testid="event-form-start"/>
            </div>
            <div>
              <Label>End *</Label>
              <Input type="datetime-local" value={form.end_datetime} onChange={e => setForm({ ...form, end_datetime: e.target.value })} data-testid="event-form-end"/>
            </div>
            <div>
              <Label>Venue</Label>
              <Input value={form.venue} onChange={e => setForm({ ...form, venue: e.target.value })}/>
            </div>
            <div>
              <Label>Trainer</Label>
              <Input value={form.trainer_name} onChange={e => setForm({ ...form, trainer_name: e.target.value })}/>
            </div>
            <div className="md:col-span-2">
              <Label>Hospital / Location</Label>
              <Select value={form.hospital_id || "__none__"} onValueChange={v => setForm({ ...form, hospital_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="event-form-hospital"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {locs.map(l => <SelectItem key={l.id} value={l.id}>{l.name}{l.code ? ` (${l.code})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.event_type === "recurring" && (
              <div className="md:col-span-2 grid grid-cols-3 gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200">
                <div>
                  <Label className="text-xs">Repeat</Label>
                  <Select value={form.recurrence?.pattern || "weekly"} onValueChange={v => setForm({ ...form, recurrence: { ...(form.recurrence || {}), pattern: v } })}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Every N</Label>
                  <Input type="number" min={1} value={form.recurrence?.interval || 1} onChange={e => setForm({ ...form, recurrence: { ...(form.recurrence || {}), interval: parseInt(e.target.value || 1) } })}/>
                </div>
                <div>
                  <Label className="text-xs">Ends on</Label>
                  <Input type="date" value={form.recurrence?.end_date || ""} onChange={e => setForm({ ...form, recurrence: { ...(form.recurrence || {}), end_date: e.target.value } })}/>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="event-form-save">{editing ? "Save changes" : "Create event"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR viewer */}
      {qrEvent && <QRPoster event={qrEvent} location={locById[qrEvent.hospital_id]} onClose={() => setQrEvent(null)}/>}

      {/* Attendees dialog */}
      <Dialog open={!!attendeesOf} onOpenChange={(v) => !v && setAttendeesOf(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{attendeesOf?.title} — Attendees</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm" data-testid="attendees-table">
              <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50 sticky top-0">
                <tr>
                  <th className="py-2 px-3">Employee</th>
                  <th className="py-2 px-3">Emp ID</th>
                  <th className="py-2 px-3">Marked at</th>
                  <th className="py-2 px-3">Source</th>
                </tr>
              </thead>
              <tbody>
                {(attendeesOf?.attendees || []).length === 0 && (
                  <tr><td colSpan="4" className="py-8 text-center text-slate-400">No attendance yet.</td></tr>
                )}
                {(attendeesOf?.attendees || []).map(a => (
                  <tr key={a.id} className="border-b border-slate-100">
                    <td className="py-2 px-3 font-medium">{a.emp_name}</td>
                    <td className="py-2 px-3 font-mono text-xs text-slate-500">{a.emp_id}</td>
                    <td className="py-2 px-3">{new Date(a.attended_at).toLocaleString()}</td>
                    <td className="py-2 px-3"><Badge variant="secondary" className="bg-sky-100 text-sky-700">{a.source}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove <b>{confirmDel?.title}</b>. Past attendance records will be kept for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => del(confirmDel)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function QRPoster({ event, location, onClose }) {
  const [qr, setQr] = useState("");
  const kioskUrl = `${window.location.origin}/kiosk/event/${event.id}`;

  useEffect(() => {
    QRCode.toDataURL(kioskUrl, { width: 380, margin: 1, color: { dark: "#0F172A", light: "#FFFFFF" } })
      .then(setQr).catch(() => {});
  }, [kioskUrl]);

  function printPoster() {
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) return;
    w.document.write(`
      <html><head><title>${event.title} — QR Poster</title>
      <style>
        body{ font-family: system-ui, -apple-system, Segoe UI, sans-serif; padding:40px; text-align:center; }
        h1{ font-size:36px; margin:0 0 8px; }
        h2{ font-size:22px; color:#0284c7; margin:0 0 24px; }
        .card{ border:2px solid #0284c7; padding:32px; border-radius:24px; max-width:520px; margin:0 auto; }
        img{ width:340px; height:340px; }
        .meta{ margin-top:20px; font-size:16px; color:#334155; line-height:1.7; }
        .code{ font-family:monospace; font-size:12px; color:#64748b; margin-top:12px; word-break: break-all; }
      </style></head><body>
      <div class="card">
        <h1>${event.title}</h1>
        <h2>Scan to mark your attendance</h2>
        <img src="${qr}" alt="QR"/>
        <div class="meta">
          <div><b>Venue:</b> ${event.venue || "—"}</div>
          <div><b>Date:</b> ${new Date(event.start_datetime).toLocaleString()}</div>
          ${location ? `<div><b>Location:</b> ${location.name}</div>` : ""}
          ${event.trainer_name ? `<div><b>Trainer:</b> ${event.trainer_name}</div>` : ""}
        </div>
        <div class="code">${kioskUrl}</div>
      </div>
      <script>setTimeout(()=>window.print(),400);</script>
      </body></html>
    `);
    w.document.close();
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5 text-sky-500"/>QR Poster</DialogTitle>
        </DialogHeader>
        <div className="text-center space-y-3" data-testid="event-qr-poster">
          <div className="font-bold text-xl text-slate-900">{event.title}</div>
          <div className="text-sm text-slate-500">Employees scan this QR at the venue and enter their Employee ID + DOB to mark attendance.</div>
          {qr ? (
            <img src={qr} alt="QR" className="mx-auto rounded-2xl shadow-lg" data-testid="event-qr-img"/>
          ) : (
            <div className="h-64 grid place-items-center text-slate-400">Generating…</div>
          )}
          <div className="text-xs font-mono text-slate-400 break-all">{kioskUrl}</div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => window.open(kioskUrl, "_blank")}><ExternalLink className="h-4 w-4 mr-1"/>Open kiosk</Button>
          <Button onClick={printPoster} className="bg-sky-500 hover:bg-sky-600"><QrCode className="h-4 w-4 mr-1"/>Print poster</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
